/*
MIT License

Copyright (c) 2020 Matthew Miller

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.

* https://github.com/MasterKale/SimpleWebAuthn/tree/master
*/

// Copied here to avoid importing SimpleWebAuthn at the low level

/**
 * Convert from a Base64URL-encoded string to an Array Buffer. Best used when converting a
 * credential ID from a JSON string to an ArrayBuffer, like in allowCredentials or
 * excludeCredentials
 */
export function base64URLStringToBuffer(base64URLString: string): ArrayBuffer {
   // Convert from Base64URL to Base64
   const base64 = base64URLString.replace(/-/g, '+').replace(/_/g, '/');
   /**
    * Pad with '=' until it's a multiple of four
    * (4 - (85 % 4 = 1) = 3) % 4 = 3 padding
    * (4 - (86 % 4 = 2) = 2) % 4 = 2 padding
    * (4 - (87 % 4 = 3) = 1) % 4 = 1 padding
    * (4 - (88 % 4 = 0) = 4) % 4 = 0 padding
    */
   const padLength = (4 - (base64.length % 4)) % 4;
   const padded = base64.padEnd(base64.length + padLength, '=');

   // Convert to a binary string
   const binary = atob(padded);

   // Convert binary string to buffer
   const buffer = new ArrayBuffer(binary.length);
   const bytes = new Uint8Array(buffer);

   for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
   }

   return buffer;
}

/**
 * Convert the given array buffer into a Base64URL-encoded string. Ideal for converting various
 * credential response ArrayBuffers to string for sending back to the server as JSON.
 */
export function bufferToBase64URLString(buffer: ArrayBuffer): string {
   const bytes = new Uint8Array(buffer);
   let str = '';

   for (const charCode of bytes) {
      str += String.fromCharCode(charCode);
   }

   const base64String = btoa(str);

   return base64String.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}
